import type {Root} from 'mdast'

import {MarkdownLine} from './markdown-line'

/**
 * Get the newlines that should be prepended before inserting content at the given position.
 * Ensures proper spacing between content and surrounding text.
 *
 * @param tree - The mdast tree
 * @param source - The full document text
 * @param offset - The position where content will be inserted
 * @returns A string of newlines ('' for no newlines, '\n' for one, '\n\n' for two)
 */
export function getNewlinePrefix(tree: Root, source: string, offset: number): string {
  const currentLine = MarkdownLine.atOffset(tree, source, offset)

  // contentStart already accounts for the caret being positioned after blockquote and list prefixes
  if (offset !== currentLine?.contentStart && offset !== currentLine?.start) return '\n\n'

  const previousLine = currentLine?.previous()

  // isBlank automatically handles empty blockquote and list lines
  if (!previousLine || previousLine?.isBlank) return ''

  return '\n'
}

/**
 * Get the newlines that should be appended to ensure that there is a blank line after the position.
 *
 * @param source - The full document text
 * @param offset - The position where content will be inserted
 * @returns A string of newlines ('' for no newlines, '\n' for one, '\n\n' for two)
 */
export function getNewlineSuffix(source: string, offset: number, forceNewline: boolean = false): string {
  // If selection is at end of line:
  if (source[offset] === '\n') {
    // If the next line is already blank, there is no need for any newlines
    if (source[offset + 1] === '\n') return ''
    // If the next line is not blank, add one newline
    return '\n'
  }

  if (offset >= source.length && !forceNewline) {
    return ''
  }

  // Otherwise, add two newlines to shift the content after the selection to a new line with a blank line between
  return '\n\n'
}
